import { ProtocolCommand } from '../service-model/protocol-command.interface';
import { ProtocolQuery } from '../service-model/protocol-query.interface';
import { SHELL_PROTOCOL_OPERATIONS_NS } from './operations/shell-protocol-namespace.const';

// Portfolio Hedging Analysis — the analysis session (CR-20260930-001) shell operations + reply DTOs.
// Operation names must equal the FLAT backend op type names
// `ETS.ShellService.Shell.Protocol.Operations.<Name>`. Ids travel as strings; instants as ISO-8601 UTC
// strings named *AtUtc (no "date"/"time" in the key, so the date reviver leaves them alone).
// Reply DTOs are camelCase to match the ShellRouter REST wire casing.

export type AnalysisSection = 'Underlyings' | 'Premium' | 'Protection';
export type AnalysisBlockStatus = 'Pending' | 'Exported' | 'Overdue' | 'Acquired' | 'Removed' | 'Orphaned';
export type AnalysisSessionStatus = 'Live' | 'Finished' | 'Aborted';

export interface AnalysisChangeDto {
    changeId: string;
    rowKey: string;
    section: AnalysisSection;
    tradingInstrumentCode: string;
    kind: number;            // 0 = Asset, 1 = Option
    side: number;            // 1 = Buy, -1 = Sell
    qty: number;
    strike: number | null;
    optionType: string;
    expiration: string;      // yyyy-MM-dd
    beforeText: string;
    afterText: string;
    groupBucketId: string;   // empty while homeless
    fillPx: number | null;
    guidancePx: number | null;
}

export interface AnalysisBlockDto {
    blockId: string;
    section: AnalysisSection;
    sequence: number;
    status: AnalysisBlockStatus;
    orderText: string;
    exportedAtUtc: string;
    acquiredAtUtc: string;
    revokeCount: number;
    changes: AnalysisChangeDto[];
}

export interface AnalysisSessionDto {
    schemaVersion: number;
    sessionId: string;
    userId: string;
    terminalId: string;
    portfolioId: string;
    comboId: string;
    status: AnalysisSessionStatus;
    endReason: string;
    beforeSnapshotJson: string;
    sheetStateJson: string;
    createdAtUtc: string;
    updatedAtUtc: string;
    endedAtUtc: string;
    blocks: AnalysisBlockDto[];
}

export interface AnalysisPendingBlockDto {
    sessionId: string;
    isOrphaned: boolean;
    block: AnalysisBlockDto;
}

/** Collars' view of one combo: locked (a live session exists) + the blocks pending on it. */
export interface AnalysisComboStateDto {
    comboId: string;
    isLocked: boolean;
    liveSessionId: string;
    pendingBlocks: AnalysisPendingBlockDto[];
}

export interface ListPendingAnalysisBlocksReply {
    schemaVersion: number;
    combos: AnalysisComboStateDto[];
}

/** The ESS message type both panels subscribe to (= the backend class name; MessageGuid 7c2e1f0a-... server-side). */
export const ANALYSIS_SESSION_CHANGED_MESSAGE_TYPE = 'AnalysisSessionChangedDto';

/** The live push (topic = the owning terminal): what changed and why; the panels re-read what they need. */
export interface AnalysisSessionChangedDto {
    topic: string;
    sessionId: string;
    comboId: string | null;
    userId: string;
    sessionStatus: AnalysisSessionStatus;
    reason: string;
    blockIds: string[];
    updatedAtUtc: string;
}

// ---- Queries

/**
 * The IN-APP notice (CR-20260930-001; never crosses the wire): the Collars panel publishes it the
 * moment its own Acquire / Clear command SUCCEEDS, so the analysis panel beside it re-reads without
 * waiting for the server push — an ESS dropout can lose that push for good (a RESTORED session drops
 * its unacked queue without replaying). The MessageBus is in-tab only by construction; panels in
 * OTHER tabs keep the push + the ESS-reconnect re-read. Same payload shape for both reasons.
 */
export const ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE = 'AnalysisSessionTouchedUIMessage';

export interface AnalysisSessionTouchedUIMessage {
    sessionId: string;
    reason: 'acquired' | 'revoked';
}

/** The caller's live session on a combo (empty = the caller's homeless session), or null. */
export class GetAnalysisSession implements ProtocolQuery {
    constructor(public readonly comboId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetAnalysisSession`;
    }
}

/** Collars' view: every accessible combo that is locked or carries pending blocks (optionally narrowed). */
export class ListPendingAnalysisBlocks implements ProtocolQuery {
    constructor(public readonly comboIds: string[] = []) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ListPendingAnalysisBlocks`;
    }
}

// ---- Commands

export class StartAnalysisSession implements ProtocolCommand {
    constructor(public readonly comboId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.StartAnalysisSession`;
    }
}

export interface AnalysisChangeInput {
    changeId: string;
    rowKey: string;
    section: AnalysisSection;
    tradingInstrumentCode: string;
    kind: number;
    side: number;
    qty: number;
    strike: number | null;
    optionType: string;
    expiration: string;
    beforeText: string;
    afterText: string;
}

export interface AnalysisBlockInput {
    blockId: string;
    section: AnalysisSection;
    sequence: number;
    changes: AnalysisChangeInput[];
}

export class ReplaceAnalysisChanges implements ProtocolCommand {
    constructor(
        public readonly sessionId: string,
        public readonly sheetStateJson: string,
        public readonly blocks: AnalysisBlockInput[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ReplaceAnalysisChanges`;
    }
}

export class ExportAnalysisBlocks implements ProtocolCommand {
    constructor(
        public readonly sessionId: string,
        public readonly blocks: { blockId: string; orderText: string }[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ExportAnalysisBlocks`;
    }
}

/** Revoke (Analysis, any scope) / Clear After State (Collars, block scope) — one op, two panels. */
export class RevokeAnalysisBlocks implements ProtocolCommand {
    constructor(
        public readonly sessionId: string,
        public readonly scope: 'block' | 'section' | 'session',
        public readonly blockId: string = '',
        public readonly section: string = ''
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.RevokeAnalysisBlocks`;
    }
}

export class AssignAnalysisSessionHome implements ProtocolCommand {
    constructor(
        public readonly sessionId: string,
        public readonly terminalId: string,
        public readonly portfolioId: string,
        public readonly newPortfolioName: string,
        public readonly newComboName: string,
        public readonly startingCapital: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.AssignAnalysisSessionHome`;
    }
}

export interface AnalysisFillInput {
    changeId: string;
    fillPx: number;
    guidancePx: number | null;
}

export class AcquireAnalysisBlocks implements ProtocolCommand {
    constructor(
        public readonly sessionId: string,
        public readonly blocks: { blockId: string; fills: AnalysisFillInput[] }[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.AcquireAnalysisBlocks`;
    }
}

export class ResetAnalysisSession implements ProtocolCommand {
    constructor(public readonly sessionId: string, public readonly choice: 'clear' | 'leave') { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ResetAnalysisSession`;
    }
}
